import { Component } from '@theme/component';
import { fetchConfig, onAnimationEnd, preloadImage } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

export const ADD_TO_CART_TEXT_ANIMATION_DURATION = 2000;

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number | undefined} */
  #animationTimeout;

  /** @type {number | undefined} */
  #cleanupTimeout;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    this.animateAddToCart();

    if (!event.target.closest('.quick-add-modal')) this.#animateFlyToCart();
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart() {
    const { addToCartButton } = this.refs;

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);

    if (!addToCartButton.classList.contains('atc-added')) {
      addToCartButton.classList.add('atc-added');
    }

    this.#animationTimeout = setTimeout(() => {
      this.#cleanupTimeout = setTimeout(() => {
        this.refs.addToCartButton.classList.remove('atc-added');
      }, 10);
    }, ADD_TO_CART_TEXT_ANIMATION_DURATION);
  }
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });

    document.addEventListener('cart:update', () => {
      this.updateCallOuts();
    }, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Handles the submit event for the product form.
   *
   * @param {Event} event - The submit event.
   */
  handleSubmit(event) {
    const form = this.querySelector('form[data-type="add-to-cart-form"]');
    if (!form || !(event.target instanceof Node) || !form.contains(event.target)) return;

    event.preventDefault();

    if (this.#timeout) clearTimeout(this.#timeout);

    const { addToCartTextError } = this.refs;

    // Check if the add to cart button is disabled and do an early return if it is
    if (this.refs.addToCartButtonContainer?.refs.addToCartButton?.getAttribute('disabled') === 'true') return;

    const qtyInput = form.querySelector('input[name="quantity"]');
    let moqVal = qtyInput?.dataset?.multiplier;
    if (!moqVal) {
      const noteEl = this.closest('.shopify-section')?.querySelector('.quntity-note[data-multiplier]');
      moqVal = noteEl?.dataset?.multiplier;
    }

    const executeSubmit = async () => {
      const formData = new FormData(form instanceof HTMLFormElement ? form : undefined);

      if (moqVal) {
        formData.append('properties[_moq]', moqVal);
      }
      
      if (window.customer && window.customer.email) {
        formData.append('properties[customer_email]', window.customer.email);
      }

      // --- Start Spicers B2B Pricing Logic Intercept ---
      let isUpdate = false;
      try {
        console.log('[ProductForm] Starting Spicers B2B Pricing Logic Intercept...');
        const variantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity')) || 1;
        const container = this.closest('.shopify-section, product-card, dialog');
        
        let material = '';
        let prm = '';
        if (container) {
          prm = container.querySelector('.prm-input')?.value || container.querySelector('[name="prm"]')?.value || container.querySelector('#prm')?.value || '';

          const materialInput = container.querySelector('.material-input');
          if (materialInput) {
             material = materialInput.value;
          } else {
             const selectedOpt = container.querySelector('option:checked');
             material = selectedOpt?.dataset?.material || '';
          }
          if (!material) {
             const hiddenIdInput = container.querySelector('input[name="id"]');
             material = hiddenIdInput?.dataset?.material || '';
          }
          if (!material) {
             material = container.querySelector('.sku')?.textContent?.replace('SKU:', '')?.trim() || '';
          }
        }
        console.log('[ProductForm] Extracted material:', material, 'prm:', prm);

        const customerSapId = window.customer?.sapId || '143282';
        const captchaKey = window.theme?.ENV?.captcha_site_key || '6LextFosAAAAAH3nCeeqSd1g50XqeAfURZOt8Z6a';
        console.log('[ProductForm] customerSapId:', customerSapId, 'captchaKey exists:', !!captchaKey);
        
        let token = '';
        if (window.grecaptcha) {
          console.log('[ProductForm] grecaptcha is available, waiting for token...');
          token = await new Promise((resolve) => {
            grecaptcha.ready(() => {
              grecaptcha.execute(captchaKey, { action: 'submit' })
                .then(t => {
                  console.log('[ProductForm] grecaptcha token received.');
                  resolve(t);
                })
                .catch((err) => {
                  console.warn('[ProductForm] grecaptcha execute failed:', err);
                  resolve('');
                });
            });
          });
        } else {
          console.warn('[ProductForm] grecaptcha is NOT available on window.');
        }

        if (token && window.theme?.ENV?.apiUrl) {
          let existingQty = 0;
          let existingKey = null;
          try {
            const cartRes = await fetch('/cart.js');
            const cartObj = await cartRes.json();
            const existingItem = cartObj.items.find(i => i.variant_id === parseInt(variantId));
            if (existingItem) {
              existingQty = parseInt(existingItem.properties?.qty) || existingItem.quantity || 0;
              existingKey = existingItem.key;
            }
          } catch (e) {
            console.warn('[ProductForm] Failed to fetch cart for existing quantity', e);
          }

          const totalQtyToQuote = existingQty + quantity;
          console.log('[ProductForm] totalQtyToQuote:', totalQtyToQuote, '(existing:', existingQty, ' + new:', quantity, ')');
          
          const session = window.ShopifyAnalytics?.lib?.user?.()?.traits?.()?.uniqToken || '';
          const cartData = {
            variants: [{ variantId: parseInt(variantId), qty: totalQtyToQuote, material, prm }],
            session,
            token: 'KIi16KkJ3HEjwydO7S582tKkESRZJsaJDlH2ckDXZ1OJjmHp6W',
            customer: customerSapId
          };
          console.log('[ProductForm] Payload prepared for Spicers API:', cartData);

          const url = `${window.theme.ENV.apiUrl}/cart?token=${token}`;
          const result = await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'ngrok-skip-browser-warning': '1' },
            body: JSON.stringify(cartData)
          }).then(r => r.json());
          
          console.log('[ProductForm] Spicers API Response:', result);

          const apiItem = Array.isArray(result) ? result[0] : result;
          if (apiItem && apiItem.properties) {
            Object.entries(apiItem.properties).forEach(([k, v]) => {
              formData.set(`properties[${k}]`, v);
            });
            formData.set('properties[qty]', totalQtyToQuote);
            if (apiItem.hmac) {
              formData.set('properties[_hmac]', apiItem.hmac);
            }
            // FORCE native Shopify quantity to 1, because the variant price is the total quote price.
            formData.set('quantity', 1);

            // If the item exists, switch to an UPDATE operation instead of ADD to prevent line item splitting
            if (existingKey) {
              formData.set('id', existingKey);
              isUpdate = true;
            }
            
            console.log(`[ProductForm] Successfully appended B2B properties to formData! Mode: ${isUpdate ? 'UPDATE' : 'ADD'}`);
          } else {
            console.warn('[ProductForm] No valid properties found in Spicers API response.');
          }
        } else {
          console.warn('[ProductForm] Skipping API call because token or apiUrl is missing.');
        }
      } catch (e) {
        console.error('[ProductForm] Spicers B2B pricing API intercept failed, continuing with native add', e);
      }
      // --- End Spicers B2B Pricing Logic ---

      const cartItemsComponents = document.querySelectorAll('cart-items-component');
      let cartItemComponentsSectionIds = [];
      cartItemsComponents.forEach((item) => {
        if (item instanceof HTMLElement && item.dataset.sectionId) {
          cartItemComponentsSectionIds.push(item.dataset.sectionId);
        }
      });
      formData.append('sections', cartItemComponentsSectionIds.join(','));

      const fetchCfg = fetchConfig('javascript', { body: formData });
      const targetEndpoint = isUpdate ? (Theme.routes.root + 'cart/change.js') : (Theme.routes.root + 'cart/add.js');

      fetch(targetEndpoint, {
        ...fetchCfg,
        headers: {
          ...fetchCfg.headers,
          Accept: 'text/html',
        },
      })
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            const errorMsg = response.description || response.message;
            window.dispatchEvent(new CartErrorEvent(this.id, errorMsg));

            if (!addToCartTextError) return;
            addToCartTextError.classList.remove('hidden');

            // Reuse the text node if the user is spam-clicking
            const textNode = addToCartTextError.childNodes[2];
            if (textNode) {
              textNode.textContent = errorMsg;
            } else {
              const newTextNode = document.createTextNode(errorMsg);
              addToCartTextError.appendChild(newTextNode);
            }

            // Create or get existing error live region for screen readers
            this.#setLiveRegionText(response.message);

            this.#timeout = setTimeout(() => {
              if (!addToCartTextError) return;
              addToCartTextError.classList.add('hidden');

              // Clear the announcement
              this.#clearLiveRegionText();
            }, 10000);

            // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
            // because our back-end still adds the max allowed amount to the cart.
            this.dispatchEvent(
              new CartAddEvent({}, this.id, {
                didError: true,
                source: 'product-form-component',
                itemCount: Number(formData.get('quantity')),
                productId: this.dataset.productId,
                carttotal: response.price,
              })
            );

            return;
          } else {
            const id = formData.get('id');
            const variantId = id?.toString() || '';

            try {
              localStorage.setItem('lastVariantAdded', variantId);
              // Call updateCallOuts after setting localStorage
              this.updateCallOuts();
            } catch (e) {
              console.error('Error storing variant ID in localStorage:', e);
            }

            if (addToCartTextError) {
              addToCartTextError.classList.add('hidden');
              addToCartTextError.removeAttribute('aria-live');
            }

            if (!id) throw new Error('Form ID is required');

            // Add aria-live region to inform screen readers that the item was added
            if (this.refs.addToCartButtonContainer?.refs.addToCartButton) {
              const addToCartButton = this.refs.addToCartButtonContainer.refs.addToCartButton;
              const addedTextElement = addToCartButton.querySelector('.add-to-cart-text--added');
              const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

              this.#setLiveRegionText(addedText);

              setTimeout(() => {
                this.#clearLiveRegionText();
              }, 5000);
            }

            this.dispatchEvent(
              new CartAddEvent({}, id.toString(), {
                source: 'product-form-component',
                itemCount: Number(formData.get('quantity')),
                productId: this.dataset.productId,
                sections: response.sections,
                carttotal: response.total_price || response.price,
              })
            );
          }
        })
        .catch((error) => {
          console.error(error);
        })
        .finally(() => {
          // add more thing to do in here if needed.
          this.updateCallOuts();
          cartPerformance.measureFromEvent('add:user-action', event);
        });
    };

    if (qtyInput && moqVal) {
      const multiplier = parseFloat(moqVal) || 1;
      const val = parseFloat(qtyInput.value) || 0;
      const safeQty = Math.round(val * 1000);
      const safeMult = Math.round(multiplier * 1000);

      if (multiplier > 0 && safeQty % safeMult !== 0) {
        let nearest_moq = Math.max(multiplier, Math.ceil(safeQty / safeMult) * multiplier);
        if (nearest_moq % 1 !== 0) nearest_moq = +nearest_moq.toFixed(2);

        const msg = `Order quantity must be in multiples of ${multiplier} for this product - would you like to update order quantity to ${nearest_moq}?`;

        if (window.vex?.dialog) {
          window.vex.dialog.confirm({
            message: msg,
            callback: (accepted) => {
              if (accepted) {
                qtyInput.value = nearest_moq;
                executeSubmit();
              } else {
                qtyInput.value = multiplier;
              }
            }
          });
        } else {
          if (confirm(msg)) {
            qtyInput.value = nearest_moq;
            executeSubmit();
          } else {
            qtyInput.value = multiplier;
          }
        }
        return;
      }
    }

    executeSubmit();
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId, addToCartButtonContainer } = this.refs;

    const currentAddToCartButton = addToCartButtonContainer?.refs.addToCartButton;
    const newAddToCartButton = event.detail.data.html.querySelector('[ref="addToCartButton"]');

    if (!currentAddToCartButton) return;

    // Update the button state
    if (event.detail.resource == null || event.detail.resource.available == false) {
      currentAddToCartButton.setAttribute('disabled', 'true');
      this.refs.acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
    } else {
      addToCartButtonContainer.refs.addToCartButton.removeAttribute('disabled');
      this.refs.acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
    }

    // Update the add to cart button text and icon
    if (newAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
    }

    // Update the variant ID
    variantId.value = event.detail.resource.id ?? '';

    // Set the data attribute for the add to cart button to the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      productVariantMedia &&
        addToCartButtonContainer?.setAttribute('data-product-variant-media', productVariantMedia + '&width=100');
    }
  };

  // Add the updateCallOuts function
  updateCallOuts() {
    const lastVariantAdded = window.localStorage.getItem('lastVariantAdded');
    const cartItemCallOut = document.querySelector(`[data-call-out="${lastVariantAdded}"]`);
    // Hide all call-outs first
    const allCallOuts = document.querySelectorAll('[data-call-out]');
    allCallOuts.forEach(callOut => callOut.classList.add('hidden'));

    // Show the call-out for the last added variant
    if (cartItemCallOut) {
      cartItemCallOut.classList.remove('hidden');
    }
  }
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}

class FlyToCart extends HTMLElement {
  /** @type {Element} */
  source;

  /** @type {Element} */
  destination;

  connectedCallback() {
    this.#animate();
  }

  #animate() {
    const rect = this.getBoundingClientRect();
    const sourceRect = this.source.getBoundingClientRect();
    const destinationRect = this.destination.getBoundingClientRect();

    //Define bezier curve points
    // Maybe add half of the size of the flying thingy to the x and y to make it center properly
    const offset = {
      x: rect.width / 2,
      y: rect.height / 2,
    };
    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2 - offset.x,
      y: sourceRect.top + sourceRect.height / 2 - offset.y,
    };

    const endPoint = {
      x: destinationRect.left + destinationRect.width / 2 - offset.x,
      y: destinationRect.top + destinationRect.height / 2 - offset.y,
    };

    //Calculate the control points
    const controlPoint1 = { x: startPoint.x, y: startPoint.y - 200 }; // Go up 200px
    const controlPoint2 = { x: endPoint.x - 300, y: endPoint.y - 100 }; // Go left 300px and up 100px

    //Animation variables
    /** @type {number | null} */
    let startTime = null;
    const duration = 600; // 600ms

    this.style.opacity = '1';

    /**
     * Animates the flying thingy along the bezier curve.
     * @param {number} currentTime - The current time.
     */
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Calculate current position along the bezier curve
      const position = bezierPoint(progress, startPoint, controlPoint1, controlPoint2, endPoint);

      //Update the position of the flying thingy
      this.style.setProperty('--x', `${position.x}px`);
      this.style.setProperty('--y', `${position.y}px`);

      // Scale down as it approaches the cart
      const scale = 1 - progress * 0.5;
      this.style.setProperty('--scale', `${scale}`);

      //Continue the animation if not finished
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        //Fade out the flying thingy
        this.style.opacity = '0';
        onAnimationEnd(this, () => this.remove());
      }
    };

    // Position the flying thingy back to the start point
    this.style.setProperty('--x', `${startPoint.x}px`);
    this.style.setProperty('--y', `${startPoint.y}px`);

    //Start the animation
    requestAnimationFrame(animate);
  }
}

/**
 * Calculates a point on a cubic Bézier curve.
 * @param {number} t - The parameter value (0 <= t <= 1).
 * @param {{x: number, y: number}} p0 - The starting point (x, y).
 * @param {{x: number, y: number}} p1 - The first control point (x, y).
 * @param {{x: number, y: number}} p2 - The second control point (x, y).
 * @param {{x: number, y: number}} p3 - The ending point (x, y).
 * @returns {{x: number, y: number}} The point on the curve.
 */
function bezierPoint(t, p0, p1, p2, p3) {
  const cX = 3 * (p1.x - p0.x);
  const bX = 3 * (p2.x - p1.x) - cX;
  const aX = p3.x - p0.x - cX - bX;

  const cY = 3 * (p1.y - p0.y);
  const bY = 3 * (p2.y - p1.y) - cY;
  const aY = p3.y - p0.y - cY - bY;

  const x = aX * Math.pow(t, 3) + bX * Math.pow(t, 2) + cX * t + p0.x;
  const y = aY * Math.pow(t, 3) + bY * Math.pow(t, 2) + cY * t + p0.y;

  return { x, y };
}

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}
